import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { TimeAttendanceMonthlyExportFromAPI } from 'types/timeAttendance/timeAttendanceTypes';

const BORDER_STYLE: Partial<ExcelJS.Borders> = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
};

const HEADER_FILL = {
  type: 'pattern' as const,
  pattern: 'solid' as const,
  fgColor: { argb: 'FFD9D9D9' },
};

const formatHourValue = (value?: number | string | null) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return 0;

  return Number(numeric.toFixed(2));
};

const convertHoursToExcelTime = (value?: number | string | null) => {
  const hours = formatHourValue(value);
  return hours / 24;
};

const getNetWorkingHoursCellValue = (value?: number | string | null) => {
  const hours = formatHourValue(value);
  if (hours < 0) return '-';
  return convertHoursToExcelTime(hours);
};

const setCommonCellStyle = (row: ExcelJS.Row) => {
  row.eachCell((cell) => {
    cell.border = BORDER_STYLE;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };
  });
};
const isZeroDate = (value?: string | null) =>
  !value || value === '0000-00-00 00:00:00' || value === '0000-00-00';

const parseDate = (value?: string | null) => {
  if (isZeroDate(value)) return null;

  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value?: string | null) => {
  const date = parseDate(value);
  if (!date) return '-';

  return [
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getFullYear()),
  ].join('/');
};

export const exportTimeAttendanceMonthlyExcel = async (
  data: TimeAttendanceMonthlyExportFromAPI[],
  getStoreNameByBranchCode: (branchCode: string) => string,
  // yyyymm: string
  startDate: string,
  endDate: string
) => {
  const formattedDateRange = `${startDate}_to_${endDate}`;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Monthly Summary');

  worksheet.columns = [
    { key: 'no', width: 8 },
    { key: 'month', width: 14 },
    { key: 'office_code', width: 16 },
    { key: 'full_name', width: 28 },
    { key: 'branch_name', width: 14 },
    // { key: 'total_work_days', width: 16 },
    { key: 'working_hours', width: 20 },
    { key: 'breaking_hours', width: 20 },
    { key: 'net_working_hours', width: 14 },
  ];

  const dateText = `${formatDate(startDate)} - ${formatDate(endDate)}`;
  // worksheet.mergeCells('A1:I1');
  worksheet.mergeCells('A1:H1');
  const titleCell = worksheet.getCell('A1');
  // titleCell.value = 'รายเดือน (Monthly Summary)';
  titleCell.value = `รายเดือน (Monthly Summary) ${dateText}`;
  titleCell.font = { bold: true, size: 12 };
  // titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.border = BORDER_STYLE;
  titleCell.fill = HEADER_FILL;

  const headerRow = worksheet.getRow(2);
  headerRow.values = [
    'NO',
    'เดือน',
    'รหัสพนักงาน',
    'ชื่อพนักงาน',
    'สาขา',
    // 'จำนวนทำงาน',
    'เวลาทำงาน (ชม:นาที)',
    'เวลาพัก (ชม:นาที)',
    'ชั่วโมงสุทธิ',
  ];
  headerRow.font = { bold: true, size: 12 };
  headerRow.height = 24;
  setCommonCellStyle(headerRow);
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL;
  });

  data.forEach((item, index) => {
    // const rowNumber = index + 2;
    const row = worksheet.addRow({
      no: index + 1,
      month: item.month || '-',
      office_code: item.office_code || '-',
      full_name: item.full_name || '-',
      branch_name: getStoreNameByBranchCode(item.branch_code),
      // total_work_days: Number(item.total_work_days ?? 0),
      working_hours: convertHoursToExcelTime(item.working_hours),
      breaking_hours: convertHoursToExcelTime(item.breaking_hours),
      net_working_hours: getNetWorkingHoursCellValue(item.net_working_hours),
      // net_working_hours: {
      //   formula: `F${rowNumber}-G${rowNumber}`,
      // },
    });

    row.height = 22;
    row.font = { size: 10 };
    setCommonCellStyle(row);

    // ['G', 'H'].forEach((columnKey) => {
    //   row.getCell(columnKey).numFmt = '[h]:mm';
    // });
    // if (formatHourValue(item.net_working_hours) >= 0) {
    //   row.getCell('I').numFmt = '[h]:mm';
    // }
    ['F', 'G'].forEach((columnKey) => {
      row.getCell(columnKey).numFmt = '[h]:mm';
    });
    if (formatHourValue(item.net_working_hours) >= 0) {
      row.getCell('H').numFmt = '[h]:mm';
    }

    row.getCell('B').numFmt = '@';
    row.getCell('C').alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true,
    };
    row.getCell('D').alignment = {
      vertical: 'middle',
      horizontal: 'left',
      wrapText: true,
    };
  });

  worksheet.views = [{ state: 'frozen', ySplit: 2 }];

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer]),
    `time_attendance_monthly_report_${formattedDateRange}.xlsx`
  );
};
